import environment from '@environments/environment';
import { TalkdeskChatSDK } from '../types/talkdesk.type';
import { User } from '@iris-sites/interfaces/sites.interfaces';

/** Talkdesk Chat SDK mounts the Live Chat FAB/widget on this body node. */
export const TALKDESK_CONTAINER_ID = 'tdWebchat';

/**
 * Toggled on document.body while the Collab Hub panel is expanded so the
 * Live Chat FAB can be hidden without unmounting Talkdesk.
 */
export const LIVE_CHAT_FAB_HIDDEN_CLASS = 'live-chat-fab-hidden';

/** Document-level style that hides `#tdWebchat` while the hub is open. */
export const LIVE_CHAT_FAB_HIDE_STYLE_ID = 'erm-live-chat-fab-hidden';

/**
 * Hides or restores the Talkdesk Live Chat FAB.
 *
 * The widget lives on document.body, outside Angular's view, so component
 * CSS cannot reach it. A document-level style covers the launcher even if
 * Talkdesk remounts `#tdWebchat` while the hub is still open.
 *
 * @param hidden - True to hide the FAB; false to restore it.
 */
export function setLiveChatFabHidden(hidden: boolean): void {
  document.body.classList.toggle(LIVE_CHAT_FAB_HIDDEN_CLASS, hidden);

  const existing = document.getElementById(LIVE_CHAT_FAB_HIDE_STYLE_ID);
  if (hidden) {
    if (existing) return;
    const styleEl = document.createElement('style');
    styleEl.id = LIVE_CHAT_FAB_HIDE_STYLE_ID;
    styleEl.textContent = `#${TALKDESK_CONTAINER_ID}{display:none!important}`;
    document.head.appendChild(styleEl);
    return;
  }
  existing?.remove();
}

export function initializeTalkdeskScript(
  user: User,
  onError?: (message: string) => void,
): void {
  try {
    // Check if script is already loaded
    if (window.TalkdeskChatSDK || !user?.id) {
      return;
    }
    // Create container div
    const divContainer = document.createElement('div');
    divContainer.id = TALKDESK_CONTAINER_ID;
    document.body.appendChild(divContainer);

    // Create script element
    const script = document.createElement('script');
    const firstScriptTag = document.getElementsByTagName('script')[0];
    script.type = 'text/javascript';
    script.charset = 'UTF-8';
    script.id = 'tdwebchatscript';
    script.src = environment.talkdesk.scriptUrl;
    script.async = true;

    // Handle script loading errors
    script.onerror = function () {
      const message = 'Failed to load Talkdesk Chat SDK script';
      console.error(message);
      onError?.(message);
    };

    // Add script to DOM
    if (firstScriptTag && firstScriptTag.parentNode) {
      firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
    } else {
      document.head.appendChild(script);
    }

    // Set up onload handler
    script.onload = function () {
      if (!window.TalkdeskChatSDK) {
        const message = 'TalkdeskChatSDK not found after script loaded';
        console.error(message);
        onError?.(message);
        return;
      }

      try {
        const webchat = (window.TalkdeskChatSDK as TalkdeskChatSDK)(
          TALKDESK_CONTAINER_ID,
          {
            touchpointId: environment.talkdesk.touchpointId,
            accountId: environment.talkdesk.accountId,
            region: environment.talkdesk.region,
          },
        );

        if (!webchat) {
          throw new Error('Failed to initialize Talkdesk Chat SDK');
        }

        const fullName =
          user?.first_name && user?.last_name
            ? `${user?.first_name} ${user?.last_name}`
            : user?.first_name || user?.last_name || '';

        // Store webchat in window for potential later use
        window.webchat = webchat;

        // Chain setContextParam after init resolves so the prefill is guaranteed
        // to be applied before the chat widget renders (avoids silent drop with showInitialScreen: true)
        webchat
          .init(environment.talkdesk.config)
          .then(() => {
            webchat.setContextParam({
              field_name1: fullName,
              field_email1: user?.email ?? '',
              chatTitle: 'Live Chat',
            });
          })
          .catch((initError: unknown) => {
            const message = `Talkdesk init failed: ${initError}`;
            console.error(message);
            onError?.(message);
          });
      } catch (initError) {
        const message = `Error initializing Talkdesk Chat: ${initError}`;
        console.error(message);
        onError?.(message);
      }
    };
  } catch (error) {
    throw new Error(`Error initializing Talkdesk script: ${error}`);
  }
}
